import {
  Alert,
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormHelperText,
  Grid,
  InputLabel,
  MenuItem,
  Select,
} from '@mui/material';
import AccountBalanceOutlined from '@mui/icons-material/AccountBalanceOutlined';
import { useTranslation } from 'react-i18next';
import CpdActionButton from '@/components/ui/CpdActionButton';
import Button from '@/components/CustomButtons/Button';
import FormErrorSummary from '@/components/FormErrorSummary/FormErrorSummary';
import ConfigurationField from './components/ConfigurationField';
import FilePrefixesSection from './components/FilePrefixesSection';
import FolderConfigurationSection from './components/FolderConfigurationSection';
import PositivePayDeliverySection from './components/PositivePayDeliverySection';
import PositivePayEncryptionSection from './components/PositivePayEncryptionSection';
import PositivePayFileHeaderSection from './components/PositivePayFileHeaderSection';
import ReconFoldersSection from './components/ReconFoldersSection';
import SectionHeader from './components/SectionHeader';
import ServerDetailsSection from './components/ServerDetailsSection';
import SftpAuthenticationSection from './components/SftpAuthenticationSection';
import { useCreateManagedAccountForm } from './hooks/useCreateManagedAccountForm';
import {
  MANAGED_ACCOUNT_PROVIDER,
  MANAGED_ACCOUNT_TYPE,
  MANAGED_ACCOUNT_PROVIDER_TRANSLATION_KEY,
  MANAGED_ACCOUNT_TYPE_TRANSLATION_KEY,
  SECTION_BOTTOM_MARGIN,
  SECTION_GRID_SPACING,
} from './managedAccount.constants';

const TypeConfigurationSections = ({ formState, disabled }) => {
  if (formState.form.type === MANAGED_ACCOUNT_TYPE.POSITIVE_PAY) {
    return (
      <>
        <PositivePayDeliverySection
          form={formState.form}
          fieldErrors={formState.fieldErrors}
          onChange={formState.updateField}
          disabled={disabled}
        />
        <PositivePayFileHeaderSection
          form={formState.form}
          fieldErrors={formState.fieldErrors}
          headerAccountConfigured={false}
          onChange={formState.updateField}
          disabled={disabled}
        />
        <PositivePayEncryptionSection
          form={formState.form}
          fieldErrors={formState.fieldErrors}
          onShouldEncryptChange={formState.updateShouldEncrypt}
          onFieldChange={formState.updateField}
          disabled={disabled}
        />
      </>
    );
  }

  if (formState.form.type === MANAGED_ACCOUNT_TYPE.PAYMENTS_MANAGER) {
    return (
      <>
        <FolderConfigurationSection
          form={formState.form}
          fieldErrors={formState.fieldErrors}
          onChange={formState.updateField}
          disabled={disabled}
        />
        <FilePrefixesSection
          form={formState.form}
          fieldErrors={formState.fieldErrors}
          onChange={formState.updateField}
          disabled={disabled}
        />
        <ReconFoldersSection
          reconPaths={formState.reconPaths}
          fieldErrors={formState.fieldErrors}
          onAdd={formState.addReconPath}
          onUpdate={formState.updateReconPath}
          onRemove={formState.removeReconPath}
          disabled={disabled}
        />
      </>
    );
  }

  return null;
};

const CreateManagedAccountDialog = ({ open, onClose, onCreated }) => {
  const { t } = useTranslation();
  const formState = useCreateManagedAccountForm({ onCreated });
  const disabled = formState.saving;

  const identityLocked = Boolean(formState.pendingConfigurationFor);

  const handleClose = () => {
    if (formState.saving) {
      return;
    }

    const created = formState.pendingConfigurationFor;
    formState.reset();

    if (created) {
      onCreated(created);
      return;
    }

    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth='lg' fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <AccountBalanceOutlined color='primary' />
          {t('managedAccounts.create.title')}
        </Box>
      </DialogTitle>

      <Box component='form' onSubmit={formState.save} noValidate>
        <DialogContent dividers>
          <FormErrorSummary errors={formState.generalErrors} />

          {identityLocked && (
            <Alert severity='warning' sx={{ mb: SECTION_GRID_SPACING }}>
              {t('managedAccounts.create.configurationPending', {
                name: formState.pendingConfigurationFor.name,
              })}
            </Alert>
          )}

          <SectionHeader>
            {t('managedAccounts.create.accountSection')}
          </SectionHeader>
          <Grid
            container
            spacing={SECTION_GRID_SPACING}
            sx={{ mb: SECTION_GRID_SPACING }}>
            <Grid item xs={12} md={4}>
              <FormControl
                fullWidth
                required
                size='small'
                disabled={disabled || identityLocked}
                error={Boolean(formState.fieldErrors.type)}>
                <InputLabel id='managed-account-type'>
                  {t('managedAccounts.create.type')}
                </InputLabel>
                <Select
                  labelId='managed-account-type'
                  label={t('managedAccounts.create.type')}
                  name='type'
                  value={formState.form.type}
                  onChange={(event) =>
                    formState.updateType(event.target.value)
                  }>
                  {Object.values(MANAGED_ACCOUNT_TYPE).map((type) => (
                    <MenuItem key={type} value={type}>
                      {t(MANAGED_ACCOUNT_TYPE_TRANSLATION_KEY[type])}
                    </MenuItem>
                  ))}
                </Select>
                <FormHelperText>
                  {formState.fieldErrors.type ||
                    t('managedAccounts.create.typeDrivesForm')}
                </FormHelperText>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth required size='small' disabled>
                <InputLabel id='managed-account-provider'>
                  {t('managedAccounts.create.provider')}
                </InputLabel>
                <Select
                  labelId='managed-account-provider'
                  label={t('managedAccounts.create.provider')}
                  name='provider'
                  value={formState.form.provider}>
                  <MenuItem value={MANAGED_ACCOUNT_PROVIDER.WELLS_FARGO}>
                    {t(
                      MANAGED_ACCOUNT_PROVIDER_TRANSLATION_KEY[
                        MANAGED_ACCOUNT_PROVIDER.WELLS_FARGO
                      ],
                    )}
                  </MenuItem>
                </Select>
                <FormHelperText>
                  {formState.fieldErrors.provider ||
                    t('managedAccounts.create.providerOnlyOption')}
                </FormHelperText>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={4}>
              <ConfigurationField
                name='name'
                label={t('managedAccounts.create.name')}
                value={formState.form.name}
                onChange={formState.updateField}
                error={formState.fieldErrors.name}
                disabled={disabled || identityLocked}
                tooltip={t('managedAccounts.create.nameTooltip')}
              />
            </Grid>
          </Grid>

          <Divider sx={{ mb: SECTION_BOTTOM_MARGIN }} />

          <ServerDetailsSection
            form={formState.form}
            fieldErrors={formState.fieldErrors}
            onChange={formState.updateField}
            disabled={disabled}
          />
          <SftpAuthenticationSection
            form={formState.form}
            fieldErrors={formState.fieldErrors}
            passwordConfigured={false}
            privateKeyConfigured={false}
            onFieldChange={formState.updateField}
            onModeChange={formState.updateAuthenticationMode}
            disabled={disabled}
          />
          <TypeConfigurationSections
            formState={formState}
            disabled={disabled}
          />
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={handleClose} disabled={disabled}>
            {identityLocked
              ? t('managedAccounts.create.configureLater')
              : t('managedAccounts.create.cancel')}
          </Button>
          <CpdActionButton type='submit' loading={formState.saving}>
            {identityLocked
              ? t('managedAccounts.create.retrySubmit')
              : t('managedAccounts.create.submit')}
          </CpdActionButton>
        </DialogActions>
      </Box>
    </Dialog>
  );
};

export default CreateManagedAccountDialog;
